import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'
import {
  BottomSheet,
  BottomSheetProps,
  BottomSheetRef,
} from 'react-spring-bottom-sheet'
import 'react-spring-bottom-sheet/dist/style.css'
import './bottom-sheet.css'
import { match } from 'ts-pattern'

export type BottomSheetConfig = BottomSheetProps & { preventDismiss?: boolean }
type BottomSheetContextType = {
  open: boolean
  config: BottomSheetProps | null
  setBottomSheetContent: (
    config: BottomSheetConfig,
    suppressOpen?: boolean,
  ) => void
  openBottomSheet: () => void
  closeBottomSheet: () => void
  sheetRef: React.RefObject<BottomSheetRef>
  snapToHeader: () => void
  sheetHeight: number | null
  headerHeight: number | null
}

const BottomSheetContext = createContext<BottomSheetContextType | undefined>(
  undefined,
)

export type DefaultSnap = 'headerHeight' | 'minHeight'

export const BottomSheetProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false)
  const [config, setConfig] = useState<BottomSheetConfig | null>(null)
  const [sheetHeight, setSheetHeight] = useState<number | null>(null)
  const [headerHeight, setHeaderHeight] = useState<number | null>(null)
  const [defaultSnap, setDefaultSnap] = useState<DefaultSnap>('minHeight')
  const sheetRef = useRef<BottomSheetRef | null>(null)

  const openBottomSheet = () => {
    setDefaultSnap('minHeight')
    setOpen(true)
  }

  const setBottomSheetContent = (
    config: BottomSheetConfig,
    suppressOpen?: boolean,
  ) => {
    setConfig(config)
    if (suppressOpen) {
      return
    }
    setOpen(true)
  }

  const updateSheetHeight = () => {
    const height = sheetRef.current?.height ?? null
    setSheetHeight(height)
  }

  const handleDefaultSnap = useCallback(
    ({
      headerHeight: actualHeaderHeight,
      minHeight,
    }: {
      headerHeight: number
      minHeight: number
    }) => {
      setHeaderHeight((prev) =>
        prev !== actualHeaderHeight ? actualHeaderHeight : prev,
      )

      return match(defaultSnap)
        .with('headerHeight', () => actualHeaderHeight)
        .with('minHeight', () => minHeight)
        .exhaustive()
    },
    [defaultSnap],
  )

  const snapToHeader = () => {
    match(open)
      .with(true, () =>
        setTimeout(() => {
          // workaround for navigation causing sheet to not snap because animation gets cancelled
          sheetRef.current?.snapTo((snaps) => snaps.headerHeight)
        }, 500),
      )
      .otherwise(() => {
        setDefaultSnap('headerHeight')
        setOpen(true)
      })
  }

  const closeBottomSheet = () => {
    setOpen(false)
    setDefaultSnap('minHeight')
    setHeaderHeight(null)
  }

  const componentConfig = { ...config }
  delete componentConfig?.preventDismiss

  return (
    <BottomSheetContext.Provider
      value={{
        open,
        config,
        openBottomSheet,
        setBottomSheetContent,
        closeBottomSheet,
        sheetRef,
        snapToHeader,
        sheetHeight,
        headerHeight,
      }}
    >
      {children}
      {config && (
        <div
          className={'bg-themed-white'}
          style={{ height: open ? headerHeight ?? 0 : 0 }}
        >
          <BottomSheet
            ref={sheetRef}
            scrollLocking={!config.preventDismiss}
            onSpringEnd={updateSheetHeight}
            defaultSnap={handleDefaultSnap}
            onDismiss={
              config.preventDismiss
                ? undefined
                : config.onDismiss ?? closeBottomSheet
            }
            {...componentConfig}
            open={open}
          >
            {config.children}
          </BottomSheet>
        </div>
      )}
    </BottomSheetContext.Provider>
  )
}

export const useBottomSheet = (): BottomSheetContextType => {
  const context = useContext(BottomSheetContext)
  if (context === undefined) {
    throw new Error('useBottomSheet must be used within a BottomSheetProvider')
  }
  return context
}
